import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { PageSpinner, Modal, EmptyState } from "../../components/ui.js";
import { IconClipboard } from "../../components/icons.js";

interface Order { id: string; name: string; calories: number | null; proteinG: number | null }
interface Restaurant { id: string; name: string; cuisine: string | null; orders: Order[] }

export function RestaurantsTab() {
  const [restaurants, setRestaurants] = useState<Restaurant[] | null>(null);
  const [addingRestaurant, setAddingRestaurant] = useState(false);
  const [orderFor, setOrderFor] = useState<Restaurant | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRestaurants((await api.get<{ restaurants: Restaurant[] }>("/api/restaurants")).restaurants);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load restaurants.");
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!restaurants) return error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />;

  return (
    <div className="stack">
      <div className="row-between">
        <p className="muted text-sm" style={{ margin: 0 }}>The places you actually eat — save your usual orders so they fit the plan, not fight it.</p>
        <button className="btn btn-sm btn-primary" onClick={() => setAddingRestaurant(true)}>Add place</button>
      </div>
      {restaurants.length === 0 ? (
        <EmptyState icon={<IconClipboard />} title="No restaurants yet">Add the spots in your rotation and their go-to orders.</EmptyState>
      ) : (
        restaurants.map((r) => (
          <div className="card" key={r.id}>
            <div className="row-between">
              <div>
                <div style={{ fontWeight: 600 }}>{r.name}</div>
                {r.cuisine && <div className="dim text-xs">{r.cuisine}</div>}
              </div>
              <button className="btn btn-sm" onClick={() => setOrderFor(r)}>+ Order</button>
            </div>
            {r.orders.length > 0 && (
              <div className="table-card mt-4" style={{ border: "none", padding: 0 }}>
                {r.orders.map((o) => (
                  <div className="tc-row" key={o.id}>
                    <span>{o.name}</span>
                    <span className="dim">{o.calories ? `${o.calories} kcal` : "—"}{o.proteinG ? ` · ${o.proteinG}g P` : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {addingRestaurant && <AddRestaurant onClose={() => setAddingRestaurant(false)} onSaved={async () => { setAddingRestaurant(false); await load(); }} />}
      {orderFor && <AddOrder restaurant={orderFor} onClose={() => setOrderFor(null)} onSaved={async () => { setOrderFor(null); await load(); }} />}
    </div>
  );
}

function AddRestaurant({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    await api.post("/api/restaurants", { name: name.trim(), cuisine: cuisine || null }).catch(() => undefined);
    onSaved();
  }
  return (
    <Modal title="Add a place" onClose={onClose}>
      <div className="field"><label>Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chipotle" /></div>
      <div className="field"><label>Cuisine (optional)</label><input className="input" value={cuisine} onChange={(e) => setCuisine(e.target.value)} placeholder="e.g. Mexican" /></div>
      <button className="btn btn-primary btn-block" onClick={save} disabled={saving || !name.trim()}>{saving ? "Saving…" : "Save"}</button>
    </Modal>
  );
}

function AddOrder({ restaurant, onClose, onSaved }: { restaurant: Restaurant; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  function num(k: string) { const v = form[k]; return v ? Number(v) : null; }
  async function save() {
    if (!form.name?.trim()) return;
    setSaving(true);
    await api.post(`/api/restaurants/${restaurant.id}/orders`, {
      name: form.name.trim(), calories: num("calories"), proteinG: num("proteinG"), carbsG: num("carbsG"), fatG: num("fatG")
    }).catch(() => undefined);
    onSaved();
  }
  return (
    <Modal title={`Order at ${restaurant.name}`} onClose={onClose}>
      <div className="field"><label>Order name</label><input className="input" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Chicken bowl, no rice" /></div>
      <div className="row wrap">
        {([["calories", "Calories"], ["proteinG", "Protein"], ["carbsG", "Carbs"], ["fatG", "Fat"]] as const).map(([k, l]) => (
          <div className="field grow" key={k} style={{ minWidth: 110, marginBottom: "var(--space-3)" }}>
            <label>{l}</label>
            <input className="input" type="number" inputMode="decimal" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      <p className="dim text-xs">Best estimates are fine — many chains publish these.</p>
      <button className="btn btn-primary btn-block mt-4" onClick={save} disabled={saving || !form.name?.trim()}>{saving ? "Saving…" : "Save order"}</button>
    </Modal>
  );
}
